import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mountain, Map, Zap, MapPin, Award } from "lucide-react";
import { useSiteData, useSectionDesign } from "../../context/SiteData";
import { useBooking } from "../../context/BookingContext";
import { Editable } from "../../context/VisualEditor";
import { HeroVideoPlayer } from "./HeroVideoPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];
// Previous Swiss hero clip — restorable default for the video player.
const DEFAULT_HERO_VIDEO = "/api/media/file/voyage-swiss/media/7b231320-89f7-443d-bd19-9a1b91c4c3de.mp4";

const BADGES = [
  { icon: Map, label: "Guided Swiss Tours" },
  { icon: Award, label: "20 Years Experience" },
  { icon: Zap, label: "Easy Booking" },
  { icon: MapPin, label: "Anywhere, Anytime" },
];

export const Hero = () => {
  const { content, locale } = useSiteData();
  const sec = useSectionDesign("hero");
  const rtl = locale === "ar";
  const { openBooking } = useBooking();
  const [offerBtn, setOfferBtn] = useState(null);

  useEffect(() => {
    axios.get(`${API}/offers`).then((r) => {
      const b = r.data.button || {};
      if (b.enabled !== false && (r.data.offers || []).length > 0) setOfferBtn(b);
    }).catch(() => {});
  }, []);

  const bg = sec.background;
  const bgUrl = bg.enabled ? bg.url : content.hero_image;
  const scrim = Math.min(1, Math.max(0, bg.scrim));
  const videoSrc = sec.video.enabled ? (sec.video.url || content.hero_video || DEFAULT_HERO_VIDEO) : null;

  return (
    <section className="relative overflow-hidden hero-grain min-h-[clamp(560px,78vh,780px)] flex items-center" dir={rtl ? "rtl" : "ltr"} data-testid="hero-section">
      {/* Full-bleed background PICTURE */}
      <div className="absolute inset-0" data-testid="hero-background">
        <img src={bgUrl} alt={content.hero_image_alt || "Swiss landscape"} className="w-full h-full object-cover"
          style={{ objectPosition: bg.position }} data-testid="hero-image" />
        {bg.overlay_opacity > 0 && (
          <div className="absolute inset-0" style={{ backgroundColor: bg.overlay_color, opacity: bg.overlay_opacity }} />
        )}
      </div>

      {/* Readability scrim — white gradient behind the heading (adjustable) */}
      <div className="absolute inset-0 hidden sm:block pointer-events-none"
        style={{ background: `linear-gradient(to ${rtl ? "left" : "right"}, rgba(255,255,255,${scrim}) 0%, rgba(255,255,255,${scrim * 0.82}) 34%, rgba(255,255,255,0) 68%)` }} data-testid="hero-scrim-desktop" />
      <div className="absolute inset-0 sm:hidden pointer-events-none"
        style={{ background: `linear-gradient(to top, rgba(255,255,255,${Math.min(1, scrim + 0.08)}) 8%, rgba(255,255,255,${scrim * 0.7}) 42%, rgba(255,255,255,0) 82%)` }} data-testid="hero-scrim-mobile" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          {/* LEFT — text + buttons */}
          <div className="max-w-xl">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-soft)] border border-[var(--brand)]/15 px-4 py-2" data-testid="hero-badge">
              <Mountain className="w-4 h-4 text-[var(--brand)]" />
              <Editable section="hero" id="eyebrow" as="span" testid="hero-badge-text" className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-[var(--brand)]">{content.hero_badge}</Editable>
            </motion.div>

            <h1 data-testid="hero-headline" className="mt-7 font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl text-[#0b132b]">
              <Editable section="hero" id="headline" as="span" className="block">{sec.text("headline", content.hero_line1)}</Editable>
              <Editable section="hero" id="headline2" as="span" className="block text-[var(--brand)]">{content.hero_line2}</Editable>
            </h1>

            <Editable section="hero" id="subtext" as="p" testid="hero-subtext"
              className="mt-6 text-base sm:text-lg text-slate-700 leading-relaxed max-w-md font-medium">
              {sec.text("subtext", content.hero_subtext)}
            </Editable>

            <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9, ease: EASE }} className="mt-9 flex flex-wrap items-center gap-3 sm:gap-4">
              <Editable section="hero" id="book_btn" as="button" testid="hero-book-tour-button" onLive={() => openBooking({ mode: "tour" })}
                className="group inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-7 py-3.5 shadow-xl shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] hover:-translate-y-0.5">
                {sec.text("button", content.hero_btn_primary)}
              </Editable>
              {offerBtn && (
                <Editable section="hero" id="offers_btn" as="button" testid="hero-special-offers-button" onLive={() => window.dispatchEvent(new CustomEvent("voyage:open-offers"))}
                  className="inline-flex items-center gap-2 rounded-full text-sm font-bold px-7 py-3.5 shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                  style={{ backgroundColor: offerBtn.bg_color || "#0b132b", color: offerBtn.text_color || "#fff" }}>
                  {rtl && offerBtn.text_ar ? offerBtn.text_ar : offerBtn.text_en || "Special Offers"}
                </Editable>
              )}
              <Editable section="hero" id="explore_btn" as="button" testid="hero-explore-tours-button" onLive={() => {
                const el = document.querySelector("#tours");
                if (!el) return;
                if (window.__lenis) window.__lenis.scrollTo(el, { offset: -84, duration: 1.4 });
                else el.scrollIntoView({ behavior: "smooth" });
              }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#0b132b]/15 bg-white/80 backdrop-blur text-sm font-bold px-7 py-3.5 text-[#0b132b] transition-all duration-300 hover:border-[var(--brand)] hover:text-[var(--brand)]">
                {content.hero_btn_secondary}
              </Editable>
            </motion.div>

            {/* Feature chips — compact single row */}
            <Editable section="hero" id="feature_rail" as="div" testid="hero-feature-rail"
              className="mt-6 flex flex-nowrap gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {BADGES.map((b, i) => (
                <span key={b.label} data-testid={`hero-rail-card-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-white/80 backdrop-blur border border-white/70 shadow-sm px-2.5 py-1.5 shrink-0">
                  <span className="w-5 h-5 rounded-full bg-[var(--brand)] flex items-center justify-center shrink-0"><b.icon className="w-3 h-3 text-white" strokeWidth={2.2} /></span>
                  <span dir="ltr" className="text-[11px] font-bold text-[#0b132b] whitespace-nowrap">{b.label}</span>
                </span>
              ))}
            </Editable>
          </div>

          {/* RIGHT — video player (nudged lower on desktop) */}
          {videoSrc && (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
              className="w-full lg:justify-self-end lg:max-w-[560px] lg:mt-20">
              <Editable section="hero" id="video_player" as="div" testid="hero-video-slot" className="w-full">
                <HeroVideoPlayer src={videoSrc} poster={bgUrl} />
              </Editable>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};
